import { useParams, Navigate, Link } from "react-router-dom";
import Reveal from "../components/Reveal";
import VideoRow from "../components/VideoRow";
import QuoteForm from "../components/QuoteForm";
import ClientMarquee from "../components/ClientMarquee";
import SEO from "../components/SEO";
import { serviceJsonLd } from "../data/seo";
import { getLocation } from "../data/locations";
import { VIDEO_PRODUCTION_FEATURES, HOME_ROWS } from "../data/content";
import VideoCard from "../components/VideoCard";
import { CheckCircle2, MapPin, Sparkles, Camera, Film, Plane, Mic, Image as ImageIcon } from "lucide-react";

const ICONS = [Mic, Camera, ImageIcon, Sparkles, Film, Plane];

export default function LocationPage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const loc = getLocation(slug);

  if (!loc) return <Navigate to="/" replace />;

  return (
    <main className="pt-28 pb-12" data-testid={`location-page-${slug}`}>
      <SEO
        title={`Video Production ${loc.city} | Yellow Video Production`}
        description={loc.metaDesc}
        path={`/${loc.slug}`}
        keywords={loc.keywords}
        jsonLd={serviceJsonLd({
          name: `Video Production ${loc.city}`,
          description: loc.metaDesc,
          path: `/${loc.slug}`,
        })}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Video Production", path: "/video-production-in-liverpool" },
          { name: loc.city, path: `/${loc.slug}` },
        ]}
      />

      {/* Hero */}
      <section className="yv-container">
        <Reveal>
          <p className="yv-eyebrow">{loc.h1Lead} · Since 2012</p>
          <h1 className="yv-h1 mt-3 max-w-5xl">
            Video production covering{" "}
            <span style={{ color: "var(--yellow)" }}>{loc.city}</span>.
          </h1>
          <p className="text-white/70 max-w-3xl mt-6 text-lg leading-relaxed">
            {loc.subhead}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/80 text-xs">
              <MapPin size={14} className="text-[var(--yellow)]" />
              {loc.cityRegion}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/10 text-white/80 text-xs">
              <Sparkles size={14} className="text-[var(--yellow)]" />
              1,000+ films delivered
            </span>
          </div>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/contact" className="yv-btn yv-btn-primary" data-testid={`loc-cta-${loc.slug}`}>
              Get a {loc.city} quote in 4 hours
            </Link>
            <Link to="/showreel" className="yv-btn yv-btn-secondary">Watch the showreel</Link>
          </div>
        </Reveal>
      </section>

      <ClientMarquee />

      {/* Intro / bullets */}
      <section className="yv-container !mt-16 grid md:grid-cols-2 gap-10 items-start">
        <Reveal>
          <p className="yv-eyebrow">Why Yellow in {loc.city}</p>
          <h2 className="yv-h2 text-white mt-2 mb-6">Full crew, fixed prices.</h2>
          <p className="text-white/70 leading-relaxed">{loc.intro}</p>
          {loc.workedWith?.length > 0 && (
            <>
              <p className="yv-eyebrow mt-8 mb-3">Selected {loc.city}-area clients</p>
              <div className="flex flex-wrap gap-2">
                {loc.workedWith.map((c) => (
                  <span key={c} className="px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/10 text-white/80 text-sm">
                    {c}
                  </span>
                ))}
              </div>
            </>
          )}
        </Reveal>
        <Reveal delay={120}>
          <ul className="space-y-3">
            {loc.points.map((p) => (
              <li key={p} className="flex gap-3 text-white/80">
                <CheckCircle2 className="text-[var(--yellow)] flex-shrink-0 mt-0.5" size={20} />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* Featured row */}
      <section className="yv-container !mt-16">
        <VideoRow
          title={`Recent work in the ${loc.city} area.`}
          eyebrow="Selected projects"
          videos={HOME_ROWS.recent.videos}
          testId={`loc-row-${loc.slug}`}
        />
      </section>

      {/* Services in region — mimic Video Production page's 6 tile grid */}
      <section className="yv-container !mt-20">
        <Reveal>
          <p className="yv-eyebrow">What we deliver in {loc.city}</p>
          <h2 className="yv-h2 text-white mt-2 max-w-3xl">
            Every video type, delivered by one crew.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10">
          {VIDEO_PRODUCTION_FEATURES.map((f, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <Reveal key={f.id} delay={i * 60}>
                <div className="bg-white/[0.02] border border-white/10 rounded-md overflow-hidden" data-testid={`loc-feature-${loc.slug}-${f.id}`}>
                  <VideoCard video={{ id: f.id, title: f.title, youtube: f.youtube }} />
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="p-2 rounded-md bg-[var(--yellow)]/10 text-[var(--yellow)]">
                        <Icon size={18} />
                      </span>
                      <h3 className="yv-h3 text-white">{f.title}</h3>
                    </div>
                    <p className="text-white/65 text-sm leading-relaxed">{f.body}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Coverage area */}
      <section className="yv-container !mt-20">
        <Reveal>
          <p className="yv-eyebrow">Areas we cover</p>
          <h2 className="yv-h2 text-white mt-2 mb-6">Filming across {loc.cityRegion}.</h2>
          <p className="text-white/65 leading-relaxed mb-6 max-w-3xl">
            Yellow regularly films across the {loc.cityRegion} area, including:
          </p>
          <div className="flex flex-wrap gap-2">
            {[loc.city, ...loc.nearby].map((n) => (
              <span
                key={n}
                className="px-4 py-2 rounded-full bg-white/[0.04] border border-white/10 text-white/80 text-sm"
                data-testid={`loc-area-${n.replace(/\s/g, "-").toLowerCase()}`}
              >
                {n}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      {/* Quote form */}
      <section className="yv-container !mt-20 max-w-2xl mx-auto">
        <Reveal>
          <QuoteForm heading={`Get a video production quote for ${loc.city}`} />
        </Reveal>
      </section>
    </main>
  );
}
